import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, AlertTriangle, Save, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { FIELDS, PRIORITY_META, computePriority } from "@/lib/triage";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

const PRIORITIES = ["P1", "P2", "P3", "P4"];

function AiBanner({ ai, hasAgent, onRerun }) {
  if (!ai) return null;
  return (
    <div className="border-b border-violet-500/20 bg-violet-500/[0.06] p-3" data-testid="ai-triage-banner">
      <div className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-violet-300">
        <Sparkles size={13} /> AI Assessment
        {ai.status === "done" && <span className="rounded px-1.5 py-0.5 text-[10px] text-white" style={{ backgroundColor: PRIORITY_META[ai.priority]?.color }}>{ai.priority}</span>}
        <button data-testid="ai-triage-rerun" onClick={onRerun} disabled={ai.status === "pending"} className="ml-auto inline-flex items-center gap-1 normal-case text-zinc-500 hover:text-zinc-200 disabled:opacity-40">
          <RefreshCw size={11} className={ai.status === "pending" ? "animate-spin" : ""} /> Újrafuttatás
        </button>
      </div>
      {ai.status === "pending" && <p className="text-[12px] text-zinc-400" data-testid="ai-triage-pending">Az AI elemzi a bejelentést…</p>}
      {ai.status === "error" && <p className="text-[12px] text-red-300" data-testid="ai-triage-error">Az AI triage nem sikerült. Próbáld újra.</p>}
      {ai.status === "done" && (
        <>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">AI Response</div>
          <p className="mt-0.5 text-[12px] leading-relaxed text-zinc-200" data-testid="ai-triage-summary">{ai.summary}</p>
          <p className="mt-1.5 text-[10px] text-zinc-500">{ai.model} · {ai.images_analyzed || 0} kép elemezve · link: {ai.link_fetched ? "megnyitva" : "nem elérhető"}{ai.link_status ? ` (${ai.link_status})` : ""} · {hasAgent ? "az agent által mentett értékelés látható lent" : "a mezők elő vannak töltve — nézd át és mentsd"}</p>
        </>
      )}
    </div>
  );
}

export function DecisionChecklist({ report, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [d, setD] = useState({});
  const [ai, setAi] = useState(report?.ai_triage || null);
  const aiData = ai?.status === "done" ? ai.data || {} : {};
  const hasAgent = !!report?.assessment?.data && !report?.assessment?.by_ai;

  useEffect(() => { setAi(report?.ai_triage || null); }, [report?.reference, report?.ai_triage]);
  useEffect(() => { setD(report?.assessment?.data || (ai?.status === "done" ? { ...ai.data } : {})); }, [report?.reference, report?.assessment, ai?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || ai?.status !== "pending") return undefined;
    const id = setInterval(() => api.get(`/mod/reports/${report.reference}/ai-triage`).then((r) => setAi(r.data.ai_triage)).catch(() => {}), 4000);
    return () => clearInterval(id);
  }, [open, ai?.status, report?.reference]);

  const rerun = async () => {
    const r = await api.post(`/mod/reports/${report.reference}/ai-triage`);
    setAi(r.data.ai_triage);
  };
  const isAi = (k, o) => (Array.isArray(aiData[k]) ? aiData[k].includes(o) : aiData[k] === o);

  const set = (k, v) => setD((p) => ({ ...p, [k]: p[k] === v ? "" : v }));
  const toggleArr = (k, val) => setD((p) => {
    const cur = Array.isArray(p[k]) ? p[k] : [];
    return { ...p, [k]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
  });

  const autoPriority = useMemo(() => computePriority(d), [d]);
  const effectivePriority = d.priority_override || autoPriority;
  const fastpath = (d.harm_category || []).includes("Child safety concern") ||
    (d.severity || "").startsWith("Critical") || (d.urgent || "").startsWith("Yes — immediate");

  const filled = Object.entries(report?.assessment?.data || {}).some(([k, v]) => k !== "priority_override" && v && (Array.isArray(v) ? v.length : true));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...d, priority: effectivePriority };
      const r = await api.patch(`/mod/reports/${report.reference}/assessment`, payload);
      onSaved?.(r.data.assessment);
      toast.success("Triage mentve");
      setOpen(false);
    } catch {
      toast.error("Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button data-testid="mod-report-checklist" variant="outline" disabled={!report} onClick={() => setOpen(true)} title="Triage checklist" className="relative h-9 gap-1.5 border-zinc-800 bg-zinc-900 px-2.5 text-zinc-300 hover:bg-zinc-800">
        <ClipboardCheck size={16} /><span className="hidden text-[13px] xl:inline">Triage</span>
        {filled ? <span className="ml-0.5 rounded-full px-1.5 text-[10px] font-bold text-white" style={{ backgroundColor: PRIORITY_META[report?.assessment?.data?.priority || "P3"]?.color }}>{report?.assessment?.data?.priority || "P3"}</span>
          : ai?.status === "done" && <span data-testid="ai-priority-chip" className="ml-0.5 inline-flex items-center gap-0.5 rounded-full border border-violet-400/50 px-1.5 text-[10px] font-bold text-violet-200"><Sparkles size={9} /> {ai.priority}</span>}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 border-zinc-800 bg-[#0d0d0f] p-0 text-zinc-100 sm:max-w-xl" data-testid="checklist-drawer">
          <SheetHeader className="border-b border-zinc-800/70 p-4">
            <SheetTitle className="flex items-center gap-2 text-zinc-50"><ClipboardCheck size={17} className="text-blue-400" /> Triage assessment</SheetTitle>
            <SheetDescription className="text-left text-[12px] text-zinc-500">{report ? <>Bejelentés: <span className="font-mono text-zinc-400">{report.reference}</span></> : ""}</SheetDescription>
          </SheetHeader>

          <AiBanner ai={ai} hasAgent={hasAgent} onRerun={rerun} />

          {/* Priority banner */}
          <div className="border-b border-zinc-800/70 bg-zinc-950 p-3" data-testid="priority-banner">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-md px-2 py-1 text-[12px] font-bold text-white" style={{ backgroundColor: PRIORITY_META[effectivePriority].color }}>{PRIORITY_META[effectivePriority].label}</span>
              <span className="text-[11px] text-zinc-500">{PRIORITY_META[effectivePriority].hint}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-zinc-500">Rendszer: {PRIORITY_META[autoPriority].label.split(" — ")[0]} · felülírás:</span>
              {["", ...PRIORITIES].map((p) => (
                <button key={p || "auto"} data-testid={`priority-${p || "auto"}`} onClick={() => setD((x) => ({ ...x, priority_override: p }))}
                  className={`rounded px-2 py-0.5 text-[11px] font-semibold ${(d.priority_override || "") === p ? "bg-blue-600 text-white" : "border border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
                  {p || "Auto"}
                </button>
              ))}
            </div>
            {fastpath && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-[12px] text-red-200" data-testid="fastpath-banner">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" /> Gyorsút: azonnali eszkaláció indokolt lehet (Critical Harms Ops / hotline) — értesítsd a senior caseworkert.
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
            {FIELDS.map((f) => (
              <section key={f.key}>
                <div className="mb-1.5 text-[12px] font-semibold text-zinc-200">{f.title}{f.type === "multi" && <span className="ml-1 text-[11px] font-normal text-zinc-500">(több is választható)</span>}</div>
                <div className="flex flex-wrap gap-1.5" data-testid={`field-${f.key}`}>
                  {f.options.map((o) => {
                    const active = f.type === "multi" ? (d[f.key] || []).includes(o) : d[f.key] === o;
                    return (
                      <button key={o} onClick={() => (f.type === "multi" ? toggleArr(f.key, o) : set(f.key, o))}
                        className={`rounded-md border px-2.5 py-1 text-left text-[12px] transition-colors ${active ? "border-blue-500/60 bg-blue-500/15 text-blue-200" : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-200"}`}>
                        {o}
                        {isAi(f.key, o) && <span data-testid={`ai-tag-${f.key}`} className="ml-1.5 rounded bg-violet-500/20 px-1 text-[9px] font-bold uppercase text-violet-300">AI</span>}
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          <div className="border-t border-zinc-800/70 p-3">
            <Button data-testid="checklist-save" onClick={save} disabled={saving || !report} className="h-10 w-full gap-2 bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-500">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Mentés ({effectivePriority})
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
